// Reusable show/hide password field. Built once here (not inline in
// LoginPage) because the buyer portal will need the identical control —
// same reasoning shared/components/SignatureElement already established
// for "build it once in shared/ and use it everywhere."
//
// - Eye/EyeOff (lucide-react), imported individually so it tree-shakes.
// - The toggle is a real <button type="button">, not a clickable <span> or
//   <div> — genuinely keyboard-reachable (native tab order, Enter/Space
//   activation) and, being type="button" specifically, never triggers a
//   surrounding <form>'s submit.
// - aria-label comes from shared/i18n, both languages — `lang` is a
//   required prop (mirroring shared/components/SignatureElement's own
//   `lang` prop) rather than assumed from a React context, since shared/
//   has no opinion on which app's LanguageContext shape is in scope.
// - `visible` starts at `false` via a plain `useState(false)` every mount,
//   with no prop, localStorage, or query-string default and nothing that
//   writes it anywhere outside this component's own state — the field is
//   never visible on mount and the shown/hidden state never survives a
//   remount (e.g. navigating away and back, or a hot reload), by
//   construction, not by convention.
// - forwardRef + prop spreading so `{...register('password')}` (react-
//   hook-form) works exactly as it would on a plain <input> — the `ref`
//   react-hook-form supplies is handled by React's own forwardRef
//   mechanism, never passed through `inputProps`.

import { forwardRef, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { t } from '../../i18n/index.js';
import styles from './PasswordInput.module.css';

export const PasswordInput = forwardRef(function PasswordInput(
  { lang, className, ...inputProps },
  ref
) {
  const [visible, setVisible] = useState(false);

  return (
    <div className={className ? `${styles.wrapper} ${className}` : styles.wrapper}>
      <input
        {...inputProps}
        ref={ref}
        type={visible ? 'text' : 'password'}
        className={styles.input}
      />
      <button
        type="button"
        className={styles.toggle}
        onClick={() => setVisible((prev) => !prev)}
        aria-label={t(visible ? 'passwordInput.hide' : 'passwordInput.show', lang)}
        aria-pressed={visible}
      >
        {visible ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
      </button>
    </div>
  );
});

export default PasswordInput;
